import { PERMISSIONS, type Permission } from '@ibe/constants';
import {
  Banknote,
  Bed,
  Bell,
  Building,
  Building2,
  CalendarDays,
  CreditCard,
  FileText,
  FileX,
  Image,
  Images,
  Key,
  LayoutDashboard,
  Package,
  Percent,
  PieChart,
  Shield,
  Sparkles,
  Users,
  UserCog,
  UserCheck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

/**
 * The sidebar, as data.
 *
 * Each entry names the permission that makes it visible, taken from
 * `@ibe/constants` rather than written as a string. A typo in a permission
 * literal hides a section from everyone, silently and permanently, and nobody
 * reports a menu item they have never seen.
 *
 * A section with no visible children is not rendered at all — an empty
 * accordion that opens onto nothing is worse than an absent one.
 *
 * **Every entry here must resolve to a real route.** This list once carried
 * twelve that did not: `/rooms/types`, `/pricing/rate-plans`, `/settings` and
 * nine more, each rendering the not-found page. A menu is a promise about what
 * the application can do, and a dead entry is worse than a missing one —
 * someone clicks it, gets "that page does not exist", and reasonably concludes
 * the product is broken rather than unfinished. `tests/navigation.test.tsx`
 * renders every entry and fails if any of them 404s, so an item added ahead of
 * its screen does not reach anyone.
 *
 * Room types and rooms are absent for a different reason, and will stay
 * absent: both are scoped to one property — the API requires it, and a room
 * number is unique within a property and nowhere else — so there is no global
 * screen for them to link to. They are reached from a property instead.
 */

export interface NavItem {
  readonly label: string;
  readonly to: string;
  /** Any one of these grants visibility. */
  readonly permissions: readonly Permission[];
  /** Optional icon shown in the sidebar. */
  readonly icon?: LucideIcon;
}

export interface NavSection {
  readonly label: string;
  readonly items: readonly NavItem[];
  /**
   * Whether this section is platform-only.
   *
   * A platform Super Administrator sits outside every tenant and operates the
   * platform itself — tenants and reference data — rather than any tenant's
   * properties, reservations or guests. Marking a section `platformOnly`
   * isolates it so a platform operator sees only the platform administration
   * surface, and a tenant user never sees it.
   */
  readonly platformOnly?: boolean;
}

export const NAVIGATION: readonly NavSection[] = [
  {
    label: 'Overview',
    items: [
      {
        label: 'Dashboard',
        to: '/dashboard',
        permissions: [PERMISSIONS.RESERVATION_READ],
        icon: LayoutDashboard,
      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      {
        label: 'Properties',
        to: '/properties',
        permissions: [PERMISSIONS.PROPERTY_READ],
        icon: Building2,
      },
      {
        label: 'Property groups',
        to: '/property-groups',
        permissions: [PERMISSIONS.PROPERTY_GROUP_READ],
        icon: Building,
      },
      {
        label: 'Room types',
        to: '/room-types',
        permissions: [PERMISSIONS.ROOM_TYPE_READ],
        icon: Bed,
      },
      {
        label: 'Amenities',
        to: '/amenities',
        permissions: [PERMISSIONS.AMENITY_READ],
        icon: Sparkles,
      },
      {
        label: 'Property images',
        to: '/property-images',
        permissions: [PERMISSIONS.PROPERTY_READ],
        icon: Images,
      },
      {
        label: 'Property setup',
        to: '/property-setup',
        permissions: [PERMISSIONS.PROPERTY_SETUP],
        icon: Wrench,
      },
      {
        label: 'Room images',
        to: '/room-images',
        permissions: [PERMISSIONS.ROOM_READ],
        icon: Image,
      },
      {
        label: 'Availability',
        to: '/availability',
        permissions: [PERMISSIONS.AVAILABILITY_READ],
        icon: Package,
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Reservations',
        to: '/reservations',
        permissions: [PERMISSIONS.RESERVATION_READ],
        icon: CalendarDays,
      },
      {
        label: 'Reservation summary',
        to: '/reservation-summary',
        permissions: [PERMISSIONS.RESERVATION_DASHBOARD],
        icon: PieChart,
      },
      {
        label: 'Guests',
        to: '/guests',
        permissions: [PERMISSIONS.GUEST_READ],
        icon: Users,
      },
    ],
  },
  {
    label: 'Revenue',
    items: [
      {
        label: 'Rate plans',
        to: '/rate-plans',
        permissions: [PERMISSIONS.PRICING_READ],
        icon: Percent,
      },
      {
        label: 'Daily rates',
        to: '/daily-rates',
        permissions: [PERMISSIONS.PRICING_READ],
        icon: Percent,
      },
      {
        label: 'Promotions',
        to: '/promotions',
        permissions: [PERMISSIONS.PROMOTION_READ],
        icon: Percent,
      },

      // Rate-shaped configuration: what a rate includes, and what happens when
      // a booking is cancelled or paid for. Inclusions now has its own
      // permissions, taken from the Inclusion catalogue.
      {
        label: 'Cancellation policies',
        to: '/cancellation-policies',
        permissions: [PERMISSIONS.CANCELLATION_POLICY_READ],
        icon: FileX,
      },
      {
        label: 'Payment policies',
        to: '/payment-policies',
        permissions: [PERMISSIONS.PAYMENT_POLICY_READ],
        icon: CreditCard,
      },
      {
        label: 'Taxes',
        to: '/taxes',
        permissions: [PERMISSIONS.TAX_READ],
        icon: Percent,
      },
      {
        label: 'Exchange rates',
        to: '/exchange-rates',
        permissions: [PERMISSIONS.EXCHANGE_RATE_READ],
        icon: Banknote,
      },
    ],
  },
  {
    label: 'Configuration',
    items: [
      {
        label: 'Payment gateways',
        to: '/payment-gateways',
        permissions: [PERMISSIONS.PAYMENT_CONFIGURE],
        icon: CreditCard,
      },
      {
        label: 'Notification gateways',
        to: '/notifications',
        permissions: [PERMISSIONS.NOTIFICATION_READ],
        icon: Bell,
      },
      {
        label: 'Notification templates',
        to: '/notification-templates',
        permissions: [PERMISSIONS.NOTIFICATION_READ],
        icon: FileText,
      },
      {
        label: 'PMS credentials',
        to: '/pms-credentials',
        permissions: [PERMISSIONS.PMS_READ],
        icon: Key,
      },
    ],
  },
  {
    label: 'Governance',
    items: [
      {
        label: 'Audit log',
        to: '/audit',
        permissions: [PERMISSIONS.AUDIT_READ],
        icon: FileText,
      },
      {
        label: 'Users',
        to: '/users',
        permissions: [PERMISSIONS.USER_READ],
        icon: UserCog,
      },
      // `/roles` has had a real screen all along and no way to reach it.
      {
        label: 'Roles',
        to: '/roles',
        permissions: [PERMISSIONS.ROLE_READ],
        icon: UserCheck,
      },
    ],
  },

  {
    label: 'Platform',
    platformOnly: true,
    items: [
      {
        label: 'Tenants',
        to: '/tenants',
        permissions: [PERMISSIONS.TENANT_READ],
        icon: Building,
      },
      {
        label: 'Tenant admins',
        to: '/tenant-admins',
        permissions: [PERMISSIONS.TENANT_READ],
        icon: Shield,
      },
    ],
  },
];
